import React from "react";
import { motion } from "framer-motion";
import { Globe, Handshake, Building2 } from "lucide-react";

const SCHOOL_BG = "https://images.pexels.com/photos/34937657/pexels-photo-34937657.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const highlights = [
  {
    icon: Globe,
    title: "350+ Schools Worldwide",
    description:
      "Our network spans prestigious institutions across the UK, US, Switzerland, and other leading boarding school destinations.",
  },
  {
    icon: Handshake,
    title: "Trusted Partner Status",
    description:
      "We maintain direct relationships with admissions teams, ensuring accurate insights and informed recommendations.",
  },
  {
    icon: Building2,
    title: "Official Test Centre",
    description:
      "Where applicable, we serve as an authorised test centre for partner schools, simplifying the assessment process.",
  },
];

const Schools = () => {
  return (
    <section
      id="schools"
      className="relative py-24 md:py-32 overflow-hidden"
      style={{
        backgroundImage: `url(${SCHOOL_BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#2B4C7E]/85" />

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-4">
            Our Network
          </p>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-medium text-white tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            A Global Network of{" "}
            <span className="text-[#C9A227]">Distinguished Schools</span>
          </h2>
          <p className="text-lg text-white/80 leading-relaxed">
            We work with schools that share our commitment to excellence, pastoral
            care, and the development of the whole child.
          </p>
        </motion.div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {highlights.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="text-center"
                data-testid={`schools-highlight-${index}`}
              >
                <div className="w-16 h-16 rounded-full bg-[#C9A227] flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <h3
                  className="text-xl md:text-2xl font-medium text-white mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {item.title}
                </h3>
                <p className="text-white/70 leading-relaxed">{item.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Discretion Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <p className="text-white/60 italic max-w-2xl mx-auto">
            School names and partnership details are shared during our private
            consultation, once we understand your child's profile and level of
            readiness.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Schools;
